import { PurchasedPoiRowDataType } from 'features/my-zone/purchased-pois/shared/types/types';
import { CreatedPoisRowDataType } from 'features/my-zone/created-pois/view-pois/types/types';
import {
    TagsRowCustomDataType,
    TagsRowDataType,
} from 'features/my-zone/tags/tags-page/types/types';
import { VenueSuggestion } from 'features/my-zone/tags/create-new-tag/types/types';
import { getReportEntityClosingDate } from 'core/services/report-entities-service/report-entities-service';
import { getPoiFullAddress } from 'utils/poi-address-formatter/poi-address-formatter';
import { ComparisonDates } from 'types/properties-data';
import type {
    Billboard,
    Chain,
    CustomEntity,
    Place,
    PlacerEntityWrapper,
    Venue,
} from '@placer-ui/types';
import { MarketPOI } from 'core/entities';

export const normalizePoiData = (
    poi: PlacerEntityWrapper<Chain | Venue | Billboard>,
): PurchasedPoiRowDataType => {
    const purchasedPoiRowDataType: PurchasedPoiRowDataType = {
        key: poi.info.id,
        poi: poi,
    };

    return purchasedPoiRowDataType;
};

type NormalizePlacesForTagsDataArgs = {
    currentPlace: PlacerEntityWrapper<Place>;
    comparedPlace: PlacerEntityWrapper<Place>;
    estimatedFootTrafficSum: number;
    dates?: ComparisonDates;
    index: number;
};

export const normalizePlacesForTagsData = ({
    currentPlace,
    comparedPlace,
    estimatedFootTrafficSum,
    dates,
    index,
}: NormalizePlacesForTagsDataArgs): TagsRowDataType => {
    const tagRowData: TagsRowDataType = {
        key: `${currentPlace.info.id}_${index}`,
        place: currentPlace,
        customData: createCustomTagRowObject({
            currentPlace,
            comparedPlace,
            estimatedFootTrafficSum,
            dates,
        }),
    };

    return tagRowData;
};

function isDataInsufficient(place: PlacerEntityWrapper<Place>) {
    return !place?.overview || !Object.keys(place.overview ?? {})?.length;
}

type CreateCustomTagRowObjectArgs = {
    currentPlace: PlacerEntityWrapper<Place>;
    comparedPlace: PlacerEntityWrapper<Place>;
    estimatedFootTrafficSum: number;
    dates?: ComparisonDates;
};
const createCustomTagRowObject = ({
    comparedPlace,
    currentPlace,
    estimatedFootTrafficSum,
    dates,
}: CreateCustomTagRowObjectArgs): TagsRowCustomDataType => {
    const tagsRowCustomData: TagsRowCustomDataType = {
        cellTooltip: '',
        visitsShare: '',
    };

    const visitsShare = currentPlace.overview?.estimated_foottraffic
        ? `${(
              (currentPlace.overview.estimated_foottraffic / estimatedFootTrafficSum) *
              100
          ).toFixed(1)}%`
        : 'N/A';

    tagsRowCustomData.visitsShare = visitsShare;

    const isCurrentDataInsufficient = isDataInsufficient(currentPlace);
    const isPreviousDataInsufficient = isDataInsufficient(comparedPlace);

    if (!currentPlace.info.purchased) {
        tagsRowCustomData.overviewToShow = undefined;
        tagsRowCustomData.cellTooltip = `This POI only shows February 2020 data.
            Purchase the full report to enable all features and access recent data`;
    } else if (isPreviousDataInsufficient && isCurrentDataInsufficient) {
        tagsRowCustomData.overviewToShow = undefined;
        tagsRowCustomData.cellTooltip = `Insufficient data for: ${dates?.selected_compare_date_value}
        & ${dates?.selected_compare_date_value}`;
    } else if (isPreviousDataInsufficient) {
        tagsRowCustomData.overviewToShow = undefined;
        tagsRowCustomData.cellTooltip = `Insufficient data for: ${dates?.selected_compare_date_value}`;
    } else if (isCurrentDataInsufficient) {
        tagsRowCustomData.overviewToShow = undefined;
        tagsRowCustomData.cellTooltip = `Insufficient data for: ${dates?.selected_date_value}`;
    } else {
        tagsRowCustomData.overviewToShow = currentPlace.overview;
    }

    tagsRowCustomData.compare = {
        overView: constructOverviewObject(currentPlace, comparedPlace),
    };

    return tagsRowCustomData;
};

function constructOverviewObject(
    currentPlace: PlacerEntityWrapper<Place>,
    comparedPlace: PlacerEntityWrapper<Place>,
): {
    estimatedFootTraffic: number | null;
    estimatedVisitsPerSq: number | null;
} {
    const venueEstimatedFootTraffic: number = currentPlace.overview?.estimated_foottraffic ?? 0;
    const venueEstimatedVisitsPerSq: number =
        (currentPlace.overview as any)?.estimatedVisitsPerSq ?? 0;
    const compereEstimatedFootTraffic: number = comparedPlace?.overview?.estimated_foottraffic ?? 1;
    const compereEstimatedVisitsPerSq: number =
        (comparedPlace?.overview as any)?.estimatedVisitsPerSq ?? 1;

    return {
        estimatedFootTraffic: compereEstimatedFootTraffic
            ? venueEstimatedFootTraffic / compereEstimatedFootTraffic - 1
            : null,
        estimatedVisitsPerSq: compereEstimatedVisitsPerSq
            ? venueEstimatedVisitsPerSq / compereEstimatedVisitsPerSq - 1
            : null,
    };
}
export const normalizeVenueSuggestion = (place: PlacerEntityWrapper<Place>): VenueSuggestion => {
    const address = MarketPOI.isMarketPOI(place.info)
        ? MarketPOI.getLocationLabel(place.info)
        : getPoiFullAddress(place);

    const venueSuggestion: VenueSuggestion = {
        id: place.info.id,
        name: place.info.name,
        address,
        type: place.info.type,
        isRecent: false,
        isCustom: place.info.is_custom_poi ?? false,
        closingDate: getReportEntityClosingDate(place.info),
    };
    return venueSuggestion;
};

export const normalizeCustomPoiData = (
    poi: PlacerEntityWrapper<CustomEntity>,
): CreatedPoisRowDataType => {
    const createdPoiRowData: CreatedPoisRowDataType = {
        key: poi.info.id,
        poi: poi,
    };

    return createdPoiRowData;
};
